import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

const StoreFooter = ({ settings = {}, storeName }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const {
        backgroundColor = '#f9f9f9',
        textColor = '#111111',
        mutedTextColor = '#4b5563',
        columns = [],
        showPoweredBy = true,
        showNewsletter = true,
        newsletterTitle = 'Get updates from us',
        newsletterSubtitle = 'Get email updates about our latest and shop specials offers',
        inputBg = '#ffffff',
        buttonBg = '#000000',
        copyrightText = `Copyright ©${new Date().getFullYear()} All rights reserved`,
    } = settings;

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        try {
            // Mocking newsletter subscription or using existing API if available
            // In a real scenario, this would call a subscription endpoint
            await new Promise(resolve => setTimeout(resolve, 1000));
            toast.success('Subscribed successfully!');
            setEmail('');
        } catch (error) {
            toast.error('Subscription failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer style={{ backgroundColor, color: textColor }} className="py-16 px-8 md:px-4 border-t border-gray-100">
            <div className="max-w-[1400px] mx-auto">
                <div className="grid grid-cols-4 lg:grid-cols-2 md:grid-cols-1 gap-12 lg:gap-8">
                    {/* Newsletter Section */}
                    {showNewsletter && (
                        <div className="lg:col-span-1 space-y-6">
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold" style={{ color: textColor }}>{newsletterTitle}</h3>
                                <p className="text-sm" style={{ color: mutedTextColor }}>{newsletterSubtitle}</p>
                            </div>
                            <form onSubmit={handleSubscribe} className="relative max-w-sm">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    className="w-full px-4 py-3 pr-12 text-sm border-none focus:ring-2 focus:ring-black rounded-full"
                                    style={{
                                        backgroundColor: inputBg,
                                        color: '#000000', // Ensure text is visible in input
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="absolute right-1 top-1 bottom-1 px-4 rounded-full flex items-center justify-center transition-opacity hover:opacity-90 disabled:opacity-50"
                                    style={{ backgroundColor: buttonBg, color: '#ffffff' }}
                                >
                                    {loading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <ArrowRight size={18} />
                                    )}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Columns */}
                    {columns.map((column, index) => (
                        <div key={index} className="space-y-6">
                            <h4 className="text-sm font-bold uppercase tracking-wider" style={{ color: textColor }}>
                                {column.title}
                            </h4>
                            <ul className="space-y-4">
                                {(column.links || []).map((link, linkIndex) => {
                                    let url = link.url;
                                    // Handle internal relative links
                                    if (url.startsWith('/') && storeName) {
                                        const host = window.location.host;
                                        const isCustomDomain = !host.includes('stores.mypursepay.com') &&
                                            !host.includes('localhost') &&
                                            !host.includes('dashboard.mypursepay.com') &&
                                            !host.includes('merchant.mypursepay.com');

                                        // Only prepend storeName if NOT on a custom domain
                                        if (!isCustomDomain && !url.startsWith(`/${storeName}`)) {
                                            url = `/${storeName}${url}`;
                                        }
                                    }
                                    return (
                                        <li key={linkIndex}>
                                            <Link
                                                to={url}
                                                className="text-sm hover:underline transition-all"
                                                style={{ color: mutedTextColor }}
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-gray-200/50 flex flex-row md:flex-col justify-between items-center gap-4">
                    <p className="text-xs" style={{ color: mutedTextColor }}>
                        {copyrightText}
                    </p>

                    {showPoweredBy && (
                        <div className="flex items-center gap-1 text-[10px]" style={{ color: mutedTextColor }}>
                            <span>Powered by</span>
                            <a href="https://mypursepay.com" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: textColor }}>
                                MyPursePay
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
};

export default StoreFooter;
